/**
 * FactsTab — list + retire infra facts.
 *
 * Layout:
 *   - Status filter (active / stale / retired)
 *   - Table grouped by `topic` (collapsible per-topic groups).
 *   - Per-row Retire action with confirm.
 *
 * The table groups by topic in Python-like fashion: we partition the
 * flat list once the API responds, then render one section per topic
 * with a collapsible header.
 */

import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';

import { ApiError, memory } from '@/helpers/ApiHelper';
import type { FactStatus, InfraFactOut } from '@/types/memory';

type StatusFilter = 'active' | 'stale' | 'retired';

export interface FactsTabProps {
  /** When false, the Retire button is hidden. Backend enforces the same gate. */
  canEdit: boolean;
}

export function FactsTab({ canEdit }: FactsTabProps): ReactElement {
  const [status, setStatus] = useState<StatusFilter>('active');
  const [facts, setFacts] = useState<InfraFactOut[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retire, setRetire] = useState<InfraFactOut | null>(null);
  const [busy, setBusy] = useState<boolean>(false);

  const refresh = useCallback(async () => {
    try {
      const data = await memory.listFacts({ status });
      setFacts(data.results);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : 'Failed to load facts');
    }
  }, [status]);

  useEffect(() => {
    setFacts(null);
    void refresh();
  }, [refresh]);

  const grouped = useMemo<Map<string, InfraFactOut[]>>(() => {
    const map = new Map<string, InfraFactOut[]>();
    for (const fact of facts ?? []) {
      const bucket = map.get(fact.topic) ?? [];
      bucket.push(fact);
      map.set(fact.topic, bucket);
    }
    return map;
  }, [facts]);

  const onConfirmRetire = useCallback(async () => {
    if (!retire) return;
    setBusy(true);
    try {
      await memory.retireFact(retire.id);
      setRetire(null);
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : 'Retire failed');
    } finally {
      setBusy(false);
    }
  }, [retire, refresh]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="overline" color="text.secondary">
          Status
        </Typography>
        <Select
          size="small"
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
          sx={{ minWidth: 140 }}
          aria-label="Filter facts by status"
        >
          <MenuItem value="active">active</MenuItem>
          <MenuItem value="stale">stale</MenuItem>
          <MenuItem value="retired">retired</MenuItem>
        </Select>
        <Chip
          label={`${facts?.length ?? 0} facts`}
          size="small"
          variant="outlined"
        />
      </Stack>

      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}

      {!error && facts == null && (
        <Typography variant="body2" color="text.secondary">
          Loading…
        </Typography>
      )}

      {!error && facts != null && facts.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No {status} facts. The daily extractor runs at 03:00 UTC.
        </Typography>
      )}

      {!error &&
        facts != null &&
        Array.from(grouped.entries()).map(([topic, rows]) => (
          <TopicGroup
            key={topic}
            topic={topic}
            rows={rows}
            canEdit={canEdit}
            onRetire={(f) => setRetire(f)}
          />
        ))}

      {retire && (
        <Dialog open onClose={() => setRetire(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Retire fact</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2">
                Retiring removes this fact from future fact-extraction merges.
                The row is kept for audit.
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {retire.topic}
              </Typography>
              <Typography variant="body2">{retire.fact}</Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRetire(null)} disabled={busy}>
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={onConfirmRetire}
              disabled={busy}
            >
              Retire
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Stack>
  );
}

interface TopicGroupProps {
  topic: string;
  rows: InfraFactOut[];
  canEdit: boolean;
  onRetire: (fact: InfraFactOut) => void;
}

function TopicGroup({ topic, rows, canEdit, onRetire }: TopicGroupProps): ReactElement {
  const [open, setOpen] = useState<boolean>(true);
  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 1, cursor: 'pointer' }}
        onClick={() => setOpen((v) => !v)}
      >
        <IconButton size="small" aria-label={open ? 'Collapse' : 'Expand'}>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {topic}
        </Typography>
        <Chip label={rows.length} size="small" variant="outlined" />
      </Stack>
      <Collapse in={open}>
        <TableContainer>
          <Table size="small" aria-label={`${topic} facts`}>
            <TableHead>
              <TableRow>
                <TableCell>Fact</TableCell>
                <TableCell align="right">Evidence</TableCell>
                <TableCell>First observed</TableCell>
                <TableCell>Last observed</TableCell>
                <TableCell>Status</TableCell>
                {canEdit && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((fact) => (
                <TableRow key={fact.id} hover>
                  <TableCell>{fact.fact}</TableCell>
                  <TableCell align="right">
                    <Tooltip
                      title={`${fact.evidence_count} supporting investigation(s)`}
                    >
                      <Chip
                        label={fact.evidence_count}
                        size="small"
                        variant="outlined"
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {new Date(fact.first_observed).toLocaleDateString()}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {new Date(fact.last_observed).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <StatusChip status={fact.status} />
                  </TableCell>
                  {canEdit && (
                    <TableCell align="right">
                      {fact.status !== 'retired' && (
                        <Tooltip title="Retire">
                          <IconButton
                            aria-label={`Retire fact ${fact.fact}`}
                            size="small"
                            color="error"
                            onClick={() => onRetire(fact)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Box>
  );
}

function StatusChip({ status }: { status: FactStatus }): ReactElement {
  const color: 'success' | 'warning' | 'default' =
    status === 'active' ? 'success' : status === 'stale' ? 'warning' : 'default';
  return (
    <Chip
      label={status}
      size="small"
      color={color}
      variant={status === 'retired' ? 'outlined' : 'filled'}
    />
  );
}
